import * as React from "react";
import { BookPlusIcon, Check, Copy, Sparkles } from "lucide-react";
import { usePromptGeneratorContext } from "@/components/feature/promptGenerator/hooks";
import * as Sentry from "@sentry/react";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { StreamMessage } from "@/views/chat/types";
import { usePromptLibraryContext } from "@/modules/promptLibrary/promptLibrary.context";

interface UserMessageButtonsProps {
  onCopyMessage: (messageId: string, content: string) => Promise<void>;
  copiedMessageId: string | null;
  message: StreamMessage;
}

export const UserMessageButtons: React.FC<UserMessageButtonsProps> = ({ message, onCopyMessage, copiedMessageId }) => {
  const { sendPromptToImprove } = usePromptGeneratorContext();
  const { openCreatePromptLibraryModal } = usePromptLibraryContext();
  const { setRightDrawerContent } = useCurrentPdf();
  const [isImprovePrompt, setIsImprovePrompt] = React.useState(false);

  const handleCopy = React.useCallback(async () => {
    if (message && message.id && message.content) {
      try {
        await onCopyMessage(message.id, message.content);
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [message, onCopyMessage]);

  const handleImprovePrompt = () => {
    if (!isImprovePrompt) {
      setIsImprovePrompt(true);
      sendPromptToImprove(message.content);
      setRightDrawerContent("promptGenerator");
    }
  };

  const handleAddToLibrary = () => {
    openCreatePromptLibraryModal(message.content, "qa");
  };

  return (
    <div className="flex justify-end">
      <button
        onClick={handleAddToLibrary}
        className="mr-1 mt-2 flex items-center self-end rounded-md bg-white px-2 py-1 text-xs text-gray-600 shadow-sm transition-colors hover:bg-gray-100"
        aria-label="Add to library"
      >
        <>
          <BookPlusIcon size={12} className="mr-1" />
          Add to library
        </>
      </button>

      <button
        onClick={handleCopy}
        className="mr-1 mt-2 flex items-center self-end rounded-md bg-white px-2 py-1 text-xs text-gray-600 shadow-sm transition-colors hover:bg-gray-100"
        aria-label="Copy message"
      >
        {copiedMessageId === message.id ? (
          <>
            <Check size={12} className="mr-1 text-green-500" />
            Copied
          </>
        ) : (
          <>
            <Copy size={12} className="mr-1" />
            Copy
          </>
        )}
      </button>

      <button
        onClick={handleImprovePrompt}
        className="mt-2 flex items-center self-end rounded-md bg-white px-2 py-1 text-xs text-gray-600 shadow-sm transition-colors hover:bg-gray-100"
        aria-label="Improve prompt"
      >
        {isImprovePrompt ? (
          <>
            <Check size={12} className="mr-1 text-green-500" />
            Sent
          </>
        ) : (
          <>
            <Sparkles size={12} className="mr-1" />
            Improve
          </>
        )}
      </button>
    </div>
  );
};
